/**
 * User List Service - Phase 4 Architecture
 *
 * Encapsulates user listing and insight aggregation.
 * Extracted from UserService to reduce single-responsibility violations.
 */

import type { Database } from "@/integrations/supabase/database.types";
import { typedSupabase } from "@/lib/typed-supabase";
import { logger } from "@/lib/logger";
import type { IService } from "@/services/core/service-registry";
import { UsersResponse } from "@/types/admin";
import { UserRow, UserWithInsights } from "./types";
import { ServiceRegistry } from "@/services/core/service-registry";

export class UserListService implements IService {
  readonly name = "user-list" as const;
  private readonly supabase = typedSupabase;

  constructor(_serviceRegistry: ServiceRegistry) {}

  async initialize(): Promise<void> {
    logger.info("👥 UserListService initialized");
  }

  async dispose(): Promise<void> {
    logger.info("👥 UserListService disposed");
  }

  async getAllUsers(page: number, limit: number): Promise<UsersResponse> {
    const start = page * limit;

    const { data: profiles, error: profilesError } = await this.supabase
      .from("profiles")
      .select("*, user_roles(*)")
      .order("created_at", { ascending: false })
      .range(start, start + limit - 1);

    const { count, error: countError } = await this.supabase
      .from("profiles")
      .select("*", { count: "exact", head: true });

    if (profilesError) {
      throw new Error(`Failed to fetch profiles: ${profilesError.message}`);
    }
    if (countError) {
      throw new Error(`Failed to count profiles: ${countError.message}`);
    }

    const usersWithEnhancedData = await Promise.all(
      (profiles || []).map(async (profile) => {
        const [managedProjectsRes, projectMemberDataRes] = await Promise.all([
          this.supabase
            .from("projects")
            .select("id, status")
            .eq("manager_id", profile.id),
          this.supabase
            .from("project_members")
            .select("role")
            .eq("client_id", profile.id),
        ]);

        const managedProjects = managedProjectsRes.data ?? [];
        const projectMemberData = projectMemberDataRes.data ?? [];

        const { data: opportunities } = await this.supabase
          .from("opportunities")
          .select("stage, value")
          .eq("sales_rep_id", profile.id);

        const { data: achievements } = await this.supabase
          .from("user_achievements")
          .select("id, name, emoji, earned_at")
          .eq("user_id", profile.id)
          .order("earned_at", { ascending: false })
          .limit(10);

        const { count: taskCount } = await this.supabase
          .from("todos")
          .select("count", { count: "exact", head: true })
          .eq("assigned_to", profile.id)
          .eq("status", "completed");

        const projectRoles: string[] = [];
        if (managedProjects.length > 0) {
          projectRoles.push("Manager");
        }
        if (projectMemberData.length > 0) {
          projectRoles.push(...new Set(projectMemberData.map(p => p.role ?? "Member")));
        }

        const totalDeals = (opportunities ?? []).filter(o => o.stage === "closed-won").length || 0;
        const totalRevenue = (opportunities ?? []).reduce((sum, o) => sum + (o.value || 0), 0) || 0;

        return {
          ...profile,
          roles: (profile.user_roles || []).map(
            (role: {
              id: string;
              role: Database["public"]["Enums"]["app_role"];
              user_id: string;
              created_at: string;
              updated_at: string;
            }) => ({
              id: role.id,
              role: role.role,
            })
          ),
          user_roles: undefined,
          project_count: managedProjects.length + projectMemberData.length,
          project_roles: projectRoles,
          sales_performance: {
            total_deals: totalDeals,
            total_revenue: totalRevenue,
            commission_tier: this.calculateCommissionTier(totalRevenue),
            conversion_rate: opportunities && opportunities.length > 0 ? (totalDeals / opportunities.length) * 100 : 0,
          },
          achievements: achievements ?? [],
          tasks_completed: taskCount ?? 0,
          engagement_score: this.calculateEngagementScore(profile, totalDeals, taskCount ?? 0),
        };
      })
    );

    return {
      users: usersWithEnhancedData as UserRow[],
      count: count ?? 0,
    };
  }

  private calculateCommissionTier(revenue: number): string {
    if (revenue >= 1000000) return "Platinum";
    if (revenue >= 500000) return "Gold";
    if (revenue >= 100000) return "Silver";
    if (revenue >= 50000) return "Bronze";
    return "Starter";
  }

  private calculateEngagementScore(profile: Database['public']['Tables']['profiles']['Row'], deals: number, tasks: number): number {
    let score = 0;

    if (profile.last_login) {
      const daysSinceLogin = Math.floor((Date.now() - new Date(profile.last_login).getTime()) / (1000 * 60 * 60 * 24));
      if (daysSinceLogin <= 1) score += 40;
      else if (daysSinceLogin <= 7) score += 30;
      else if (daysSinceLogin <= 30) score += 20;
      else score += 10;
    }

    score += Math.min(deals * 5, 30);
    score += Math.min(tasks * 2, 30);

    return Math.min(score, 100);
  }

  async getUsersWithInsights(page: number, limit: number): Promise<UserWithInsights[]> {
    const start = page * limit;

    const client = this.supabase as unknown as {
      from: (table: string) => {
        select: (columns: string) => {
          order: (column: string, opts: { ascending: boolean }) => {
            range: (from: number, to: number) => Promise<{ data: unknown[]; error: { message: string } | null }>;
          };
        };
      };
    };
    const { data: users, error } = await client
      .from("profiles")
      .select(`
        id,
        username,
        full_name,
        avatar_url,
        email,
        is_suspended,
        created_at,
        updated_at,
        last_login,
        last_task_completed_at,
        current_streak,
        longest_streak,
        posts:posts(count),
        comments:comments(count),
        likes:likes(count),
        user_roles:user_roles(id, role)
      `)
      .order("created_at", { ascending: false })
      .range(start, start + limit - 1);

    if (error) {
      throw new Error(`Failed to fetch users with insights: ${error.message}`);
    }

    interface RawUserRow {
      id: string;
      username: string | null;
      full_name: string | null;
      avatar_url: string | null;
      email: string | null;
      is_suspended: boolean | null;
      created_at: string;
      updated_at: string | null;
      last_login: string | null;
      last_task_completed_at: string | null;
      current_streak: number | null;
      longest_streak: number | null;
      user_roles: Array<{ id: string; role: string }> | { id: string; role: string };
      posts: Array<{ count: number }> | { count: number };
      comments: Array<{ count: number }> | { count: number };
      likes: Array<{ count: number }> | { count: number };
    }

    return ((users || []) as unknown as RawUserRow[]).map((u) => {
      const userRoles = Array.isArray(u.user_roles) ? u.user_roles : (u.user_roles ? [u.user_roles] : []);
      const posts = Array.isArray(u.posts) ? u.posts : (u.posts ? [u.posts] : []);
      const comments = Array.isArray(u.comments) ? u.comments : (u.comments ? [u.comments] : []);
      const likes = Array.isArray(u.likes) ? u.likes : (u.likes ? [u.likes] : []);
      const result: UserWithInsights = {
        id: u.id,
        username: u.username ?? undefined,
        full_name: u.full_name ?? undefined,
        avatar_url: u.avatar_url ?? undefined,
        email: u.email ?? undefined,
        is_suspended: !!u.is_suspended,
        created_at: u.created_at,
        updated_at: u.updated_at ?? u.created_at,
        last_login: u.last_login ?? undefined,
        last_task_completed_at: u.last_task_completed_at ?? undefined,
        current_streak: u.current_streak ?? 0,
        longest_streak: u.longest_streak ?? 0,
        user_roles: userRoles,
        posts: posts,
        comments: comments,
        likes: likes,
        activity_score: 0,
        post_count: posts[0]?.count || 0,
        comment_count: comments[0]?.count || 0,
        like_count: likes[0]?.count || 0,
      };
      return result;
    });
  }
}
